"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { ErrorBoundary } from "react-error-boundary";
import { appUrls } from "@/constants";
import { useTranslations } from "next-intl";
import IconImageSparkle from "@ui/central-icons/icons/IconImageSparkle";
import IconSparkle from "@ui/central-icons/icons/IconSparkle";
import IconImages1 from "@ui/central-icons/icons/IconImages1";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { logger } from "@utilities/logger";
import { Button } from "@ui/components/core/Button";
import { SignUpPopover } from "@ui/components/core/SignUpPopover";
import type { FlowTemplateExample, FlowTemplateSchema } from "@/app/_api/flowTemplatesServer";
import { USER_INPUT_PLACEHOLDER } from "../constants";
import { useCreativeTemplate } from "./hooks/useCreativeTemplate";
import { HeroOmniboxCard } from "./HeroOmniboxCard";
import { HeroLayout } from "./HeroLayout";
import { DynamicHeroMedia, type StaticHeroMediaProps } from "./DynamicHeroMedia";
import { TransparencyCheckerboard } from "./TransparencyCheckerboard";
import { getModelDisplayName, type BreadcrumbLink, type GenerationModel, type WorkflowInputConfig } from "./types";

export interface HeroOmniboxClientProps {
  heroTitle: string;
  heroDescription: string;
  breadcrumbs: BreadcrumbLink[];
  templateSlug: string;
  templateCategory: string;
  variantSlug: string;
  config: WorkflowInputConfig;
  generationPrompt: string | null;
  generationModel: GenerationModel;
  staticMedia: StaticHeroMediaProps;
  redirectUrlTemplate: string;
  ctaUrl: string;
  flowTemplate: FlowTemplateSchema | null;
  flowTemplateExample: FlowTemplateExample | null;
}

function HeroOmniboxFallback({ ctaUrl }: { ctaUrl: string }) {
  const t = useTranslations("ClientKeys.creative_templates");
  return (
    <div className='tw-flex tw-w-full tw-min-h-[160px] tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-rounded-[30px] tw-bg-white tw-p-6 tw-shadow-[0px_0px_1px_0px_rgba(0,0,0,0.4),0px_1px_2px_0px_rgba(0,0,0,0.04),0px_2px_2px_0px_rgba(0,0,0,0.04)]'>
      <p className='tw-type-base tw-text-black/60'>{t("error_generic")}</p>
      <Button
        variant='primary'
        size='md'
        onClick={() => {
          window.location.href = ctaUrl;
        }}
      >
        {t("rate_limit_button")}
      </Button>
    </div>
  );
}

export function HeroOmniboxClient(props: HeroOmniboxClientProps) {
  return (
    <ErrorBoundary
      fallback={<HeroOmniboxFallback ctaUrl={props.ctaUrl} />}
      onError={(error: Error, info: React.ErrorInfo) => {
        logger.error(error, "[HeroOmnibox] crashed", info.componentStack);
      }}
    >
      <HeroOmniboxClientInner {...props} />
    </ErrorBoundary>
  );
}

function HeroOmniboxClientInner({
  heroTitle,
  heroDescription,
  breadcrumbs,
  templateSlug,
  templateCategory,
  variantSlug,
  config,
  generationPrompt,
  generationModel,
  staticMedia,
  redirectUrlTemplate,
  flowTemplate,
  flowTemplateExample,
}: HeroOmniboxClientProps) {
  const t = useTranslations("ClientKeys.creative_templates");
  const [showSignUpPopover, setShowSignUpPopover] = useState(false);

  const {
    step,
    userInput,
    resultUrl,
    redirectCtaUrl,
    inputPreviewUrl,
    selectedFile,
    error,
    handleFileSelected,
    handleTextSubmit,
    handleSubmit,
    handleRemoveFile,
    handleFlowSubmit,
    validateFile,
    restart,
  } = useCreativeTemplate({
    templateSlug,
    templateCategory,
    variantSlug,
    generationModel,
    config,
    redirectUrlTemplate,
    promptRequiresInput: generationPrompt?.includes(USER_INPUT_PLACEHOLDER) ?? false,
    flowTemplate,
  });

  useEffect(() => {
    if (step === "result") {
      setShowSignUpPopover(true);
    }
  }, [step]);

  return (
    <>
      <HeroLayout
        breadcrumbs={breadcrumbs}
        heroTitle={heroTitle}
        heroDescription={heroDescription}
        actions={
          <HeroOmniboxCard
            config={config}
            step={step}
            error={error}
            generationPrompt={generationPrompt}
            userInput={userInput}
            generationModel={generationModel}
            modelDisplayName={getModelDisplayName(generationModel)}
            selectedFile={selectedFile}
            inputPreviewUrl={inputPreviewUrl}
            redirectCtaUrl={redirectCtaUrl}
            templateCategory={templateCategory}
            variantSlug={variantSlug}
            flowTemplate={flowTemplate}
            flowTemplateExample={flowTemplateExample}
            validateFile={validateFile}
            onFileSelected={handleFileSelected}
            onTextSubmit={handleTextSubmit}
            onSubmit={handleSubmit}
            onRemoveFile={handleRemoveFile}
            onRestart={restart}
            onSignUpClick={() => setShowSignUpPopover(true)}
            onFlowSubmit={handleFlowSubmit}
          />
        }
        media={
          <DynamicHeroMedia
            step={step}
            resultUrl={resultUrl}
            inputPreviewUrl={inputPreviewUrl}
            inputIsVideo={config.acceptedMimeTypes.startsWith("video/") || config.acceptedMimeTypes === "video/*"}
            resultType={config.resultType}
            staticMedia={staticMedia}
            onDownloadClick={() => setShowSignUpPopover(true)}
          />
        }
      />
      <SignUpPopover
        isOpen={showSignUpPopover}
        onOpenChange={setShowSignUpPopover}
        cover={
          config.resultType === "image" && resultUrl ? (
            <div className='tw-relative tw-h-full tw-w-full'>
              <TransparencyCheckerboard />
              <Image src={resultUrl} alt='Generated result' fill className='tw-relative tw-object-cover' />
            </div>
          ) : undefined
        }
        title={t("signup_popover_title")}
        featureItems={[
          {
            icon: <IconImageSparkle className={`${iconStrokeFix} tw-size-[18px]`} />,
            text: t("signup_popover_feature_1"),
          },
          { icon: <IconSparkle className={`${iconStrokeFix} tw-size-[18px]`} />, text: t("signup_popover_feature_2") },
          { icon: <IconImages1 className={`${iconStrokeFix} tw-size-[18px]`} />, text: t("signup_popover_feature_3") },
        ]}
        ctaText={t("signup_popover_cta")}
        ctaHref={redirectCtaUrl ?? appUrls.imageVideo}
      />
    </>
  );
}
